import { defineStore } from "pinia";
import { ref, onMounted } from "vue";
import { supabase } from "../lib/supabase";
import type { User } from "@supabase/supabase-js";
import { buildPortalAuthCallbackUrl, sanitizeRedirectTarget } from "../utils/authNavigation";
import { captureError } from "../lib/sentry";
import { ensureProfileMeta, markProfileCompleted, profileMeta, resetProfileMeta } from "../utils/profileMeta";

export type { ProfileMeta } from "../utils/profileMeta";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Initialize auth state
  onMounted(async () => {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.warn("Session recovery failed, clearing stale auth state:", sessionError.message);
        await supabase.auth.signOut();
        user.value = null;
      } else {
        user.value = session?.user ?? null;
      }

      // Listen for auth state changes
      supabase.auth.onAuthStateChange((_event, session) => {
        const nextUser = session?.user ?? null;
        if (nextUser?.id !== user.value?.id) {
          resetProfileMeta();
        }
        user.value = nextUser;
      });
    } catch (err) {
      console.error("Error initializing auth state:", err);
      error.value = err instanceof Error ? err.message : String(err);
      // Clear potentially corrupted auth state
      user.value = null;
    }
  });

  const login = async ({ email, password }: { email: string; password: string }) => {
    try {
      loading.value = true;
      error.value = null;

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      user.value = data.user;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const signInWithMagicLink = async (email: string, redirectUrl?: string) => {
    try {
      loading.value = true;
      error.value = null;

      // Call GoTrue's core /auth/v1/otp endpoint directly instead of the custom
      // `send-auth-link-email` edge function. That earlier browser -> /functions/
      // fetch intermittently rejected on iOS/Mobile Safari (`FunctionsFetchError`,
      // Sentry PORTAL-VUE-5/-B) due to edge-gateway 5xx and Cloudflare / iCloud
      // Private Relay challenges. signInWithOtp hits the robust core auth endpoint;
      // the branded email is then sent server-to-server by the Send Email auth hook
      // (auth-send-email-hook), so the fragile browser dependency is gone.
      const emailRedirectTo = buildPortalAuthCallbackUrl({
        redirect: sanitizeRedirectTarget(redirectUrl || "/"),
        email,
      });

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo,
          shouldCreateUser: false,
        },
      });

      if (otpError) {
        throw otpError;
      }
    } catch (err) {
      console.error("Magic link error:", err);
      error.value = err instanceof Error ? err.message : String(err);
      // Auth pages swallow this into a toast/message, so report it explicitly.
      captureError(err, { flow: "signInWithMagicLink", email });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      loading.value = true;
      error.value = null;
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        const { error: authError } = await supabase.auth.signOut();
        if (authError && authError.message !== "Auth session missing!") {
          throw authError;
        }
      }
    } catch (err) {
      if (err instanceof Error && err.message === "Auth session missing!") {
        console.warn("Auth session missing during logout; clearing local state.");
      } else {
        error.value = err instanceof Error ? err.message : String(err);
        console.error("Error logging out:", err);
        throw err;
      }
    } finally {
      user.value = null;
      resetProfileMeta();
      loading.value = false;
    }
  };

  const checkUser = async () => {
    try {
      loading.value = true;
      const response = await supabase.auth.getSession();
      if (response?.data?.session) {
        user.value = response.data.session.user;
        return { data: { session: response.data.session }, error: null };
      }
      return { data: { session: null }, error: null };
    } catch (error) {
      console.error("Error checking user:", error);
      return { data: { session: null }, error };
    } finally {
      loading.value = false;
    }
  };

  const requestOtp = async (email: string) => {
    try {
      console.log("Requesting OTP for email:", email);
      loading.value = true;
      error.value = null;
      const { data, error: authError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          // Don't set emailRedirectTo to force OTP instead of magic link
        },
      });
      console.log("OTP request response:", { data, error: authError });
      if (authError) throw authError;
      console.log("OTP request successful");
    } catch (err) {
      console.error("OTP request failed:", err);
      error.value = err instanceof Error ? err.message : String(err);
      captureError(err, { flow: "requestOtp", email });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loginWithOtp = async (email: string, token: string) => {
    try {
      loading.value = true;
      error.value = null;
      const { data, error: authError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (authError) throw authError;
      user.value = data.user;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      captureError(err, { flow: "loginWithOtp", email });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    user,
    loading,
    error,
    profileMeta,
    ensureProfileMeta,
    markProfileCompleted,
    login,
    logout,
    checkUser,
    signInWithMagicLink,
    requestOtp,
    loginWithOtp,
    isAuthenticated: () => !!user.value,
  };
});
